import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X, Sparkles, ChevronLeft, ChevronRight, CheckCircle2, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import PredictedGradesPicker from "@/components/app/profile/PredictedGradesPicker";
import { useCurrency } from "@/context/CurrencyContext";

const SECTION_COPY = {
  basics: {
    fields: [
      { key: "year_of_entry",  label: "Which year do you plan to start?",   type: "number",   placeholder: "e.g. 2027" },
      { key: "curriculum",     label: "Which curriculum are you taking?",   type: "select",
        options: [
          { value: "a_level",    label: "A-Level (UK)" },
          { value: "ib",         label: "International Baccalaureate" },
          { value: "us-hs",      label: "US High School" },
          { value: "cbse",       label: "CBSE (India)" },
          { value: "isc",        label: "ISC (India)" },
          { value: "atar",       label: "ATAR (Australia)" },
          { value: "abitur",     label: "Abitur (Germany)" },
          { value: "other",      label: "Other" },
        ]
      },
      { key: "intended_degree", label: "What do you want to study?",         type: "text",    placeholder: "e.g. Computer Science" },
    ],
  },
  grades: {
    fields: [
      { key: "predicted_grades", label: "Add each subject with its predicted grade", type: "predicted_grades" },
    ],
  },
  budget: {
    fields: [
      { key: "max_annual_tuition_usd", label: "Max annual tuition",            type: "currency_optional" },
      { key: "scholarship_required",   label: "Do you need a scholarship?",    type: "scholarship_gate" },
    ],
  },
  goals: {
    fields: [
      { key: "career_direction", label: "In one sentence, what do you want your degree to help you become?",
        type: "textarea", placeholder: "e.g. A machine-learning engineer at a climate startup" },
    ],
  },
  preferences: {
    fields: [
      { key: "campus_pref",       label: "Campus type",     type: "select",
        options: [
          { value: "either", label: "No preference" },
          { value: "urban",  label: "Urban / city" },
          { value: "campus", label: "Traditional campus" },
        ]
      },
      { key: "teaching_size_pref", label: "Big lectures (1) vs Small tutorials (5)", type: "slider" },
    ],
  },
  identity: {
    fields: [
      { key: "first_name",  label: "First name",  type: "text",  placeholder: "Alex" },
      { key: "nationality", label: "Nationality", type: "text",  placeholder: "e.g. UK" },
    ],
  },
};

/**
 * Progressive Profile Completeness — small dismissible card that
 * nudges the student to answer 1-2 questions from the highest-value
 * missing section. Sits at the top of `/shortlist`.
 */
export default function ProfileCompleteness() {
  const [data, setData]         = useState(null);
  const [profile, setProfile]   = useState({});
  const [answers, setAnswers]   = useState({});
  const [manualKey, setManualKey] = useState(null); // user-picked section overrides API next
  const [saving, setSaving]     = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [dismissed, setDismissed]   = useState(false);
  const [matchCount, setMatchCount] = useState(null); // # of shortlisted courses matching career_direction

  const refresh = async () => {
    try {
      const [c, s, p] = await Promise.all([
        api.get("/profile/completeness"),
        api.get("/shortlist").catch(() => ({ data: { items: [] } })),
        api.get("/profile").catch(() => ({ data: {} })),
      ]);
      setData(c.data);
      setProfile(p.data || {});
      const items = s.data?.items || [];
      const hits = items.filter(i => i?.career_direction_match?.matched?.length > 0).length;
      setMatchCount({ hits, total: items.length });
      const d = c.data?.dismissed_at ? new Date(c.data.dismissed_at) : null;
      if (d && (Date.now() - d.getTime()) < 14 * 24 * 3600 * 1000) {
        setDismissed(true);
      }
    } catch {
      /* not logged in */
    }
  };
  useEffect(() => { refresh(); }, []);

  const saveAnswers = async (e) => {
    e?.preventDefault();
    if (!Object.keys(answers).length) return;
    setSaving(true);
    try {
      await api.post("/profile", answers);
      setAnswers({});
      setManualKey(null);  // let the server nudge the next section again
      await refresh();
      // Instant re-rank on the shortlist — see Shortlist.jsx listener.
      window.dispatchEvent(new CustomEvent("wua:profile-updated"));
    } finally { setSaving(false); }
  };

  const dismiss = async () => {
    setDismissing(true);
    try {
      await api.post("/profile/dismiss-onboarding", {});
      setDismissed(true);
    } finally { setDismissing(false); }
  };

  // Ordered section list — mirrors backend `_SECTIONS` priority order
  // via the SECTION_COPY key order.
  const orderedKeys = useMemo(() => Object.keys(SECTION_COPY), []);
  const sectionMetaByKey = useMemo(() => {
    const map = {};
    (data?.sections || []).forEach(s => { map[s.key] = s; });
    return map;
  }, [data]);

  const activeKey = manualKey || data?.next_section?.key || orderedKeys[0];
  const activeIdx = orderedKeys.indexOf(activeKey);
  const activeMeta = sectionMetaByKey[activeKey];
  const copy = SECTION_COPY[activeKey];
  const canPrev = activeIdx > 0;
  const canNext = activeIdx >= 0 && activeIdx < orderedKeys.length - 1;

  const goPrev = () => { if (canPrev) { setManualKey(orderedKeys[activeIdx - 1]); setAnswers({}); } };
  const goNext = () => { if (canNext) { setManualKey(orderedKeys[activeIdx + 1]); setAnswers({}); } };

  if (!data || dismissed) return null;
  if (data.percent >= 100)  return null; // celebrate elsewhere

  return (
    <div
      data-testid="profile-completeness"
      className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-5 mb-6 shadow-sm relative"
    >
      <button
        type="button"
        onClick={dismiss}
        disabled={dismissing}
        aria-label="Dismiss for 14 days"
        data-testid="profile-completeness-dismiss"
        className="absolute top-3 right-3 p-1 rounded hover:bg-white/70 text-stone-400 hover:text-stone-700 disabled:opacity-40"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-center gap-3 mb-3">
        <div className="relative flex-none">
          <svg width="54" height="54" viewBox="0 0 44 44" className="-rotate-90">
            <circle cx="22" cy="22" r="18" fill="none" stroke="#d1fae5" strokeWidth="4" />
            <circle
              cx="22" cy="22" r="18" fill="none" stroke="#059669" strokeWidth="4"
              strokeDasharray={`${(data.percent / 100) * 2 * Math.PI * 18} ${2 * Math.PI * 18}`}
              strokeLinecap="round"
            />
          </svg>
          <span
            data-testid="profile-completeness-percent"
            className="absolute inset-0 flex items-center justify-center text-[13px] font-semibold text-emerald-900"
          >
            {Math.round(data.percent)}%
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-underground-sc tracking-widest text-emerald-700 mb-1">
            <Sparkles className="w-3 h-3 inline-block mr-1" />
            Sharper matches
          </div>
          <div className="text-sm text-stone-800 leading-snug">
            {data.percent < 30
              ? "Your profile is nearly empty — a few quick answers will dramatically sharpen your matches."
              : data.percent < 60
              ? "Great start. A couple more answers and your shortlist gets noticeably more relevant."
              : "You're most of the way there — finish up to unlock the Fast Apply prep tools."}
          </div>
          {matchCount?.hits > 0 && (
            <div
              data-testid="profile-completeness-match-rollup"
              className="mt-1 text-[12px] text-emerald-800 leading-snug"
            >
              ✨ <span className="font-semibold">{matchCount.hits}</span>
              {" "}of {matchCount.total} shortlisted{" "}
              course{matchCount.total === 1 ? "" : "s"} now match{matchCount.hits === 1 ? "es" : ""} your goal.
            </div>
          )}
        </div>
      </div>

      {copy && (
        <form onSubmit={saveAnswers} data-testid="profile-completeness-form" className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={goPrev}
              disabled={!canPrev}
              aria-label="Previous section"
              data-testid="profile-completeness-prev"
              className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-stone-300 bg-white text-stone-600 hover:bg-stone-50 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="text-[13px] font-semibold text-stone-900" data-testid="profile-completeness-section-title">
              {activeMeta?.title || activeKey}
            </div>
            {activeMeta?.complete && (
              <span
                data-testid="profile-completeness-section-complete"
                className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold px-2 py-0.5"
              >
                <CheckCircle2 className="w-3 h-3" />
                Done — tweak below
              </span>
            )}
            <button
              type="button"
              onClick={goNext}
              disabled={!canNext}
              aria-label="Next section"
              data-testid="profile-completeness-next"
              className="ml-auto inline-flex items-center justify-center h-7 w-7 rounded-md border border-stone-300 bg-white text-stone-600 hover:bg-stone-50 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <span className="text-[10px] text-stone-400 font-mono">
              {activeIdx + 1}/{orderedKeys.length}
            </span>
          </div>
          <div className="text-[12px] text-stone-600 leading-relaxed">
            {activeMeta?.prompt || ""}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            {copy.fields.map(f => {
              const displayed = answers[f.key] !== undefined ? answers[f.key] : profile[f.key];
              return (
                <FieldInput
                  key={f.key}
                  field={f}
                  value={displayed}
                  onChange={(v) => setAnswers(a => ({ ...a, [f.key]: v }))}
                />
              );
            })}
          </div>

          <div className="flex items-center gap-3 pt-1">
            <button
              type="submit"
              disabled={saving || !Object.keys(answers).length}
              data-testid="profile-completeness-save"
              className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-stone-300 text-white text-[13px] font-medium px-3 py-1.5 rounded-md transition-colors"
            >
              {saving ? "Saving…" : "Save answer"}
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <Link
              to="/profile"
              data-testid="profile-completeness-full-form"
              className="text-[12px] text-emerald-700 hover:underline"
            >
              or fill in the full profile →
            </Link>
          </div>
        </form>
      )}
    </div>
  );
}

function FieldInput({ field, value, onChange }) {
  const cls = "w-full border border-stone-300 rounded-md px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-emerald-500";

  if (field.type === "currency_optional") {
    return (
      <CurrencyOptionalInput field={field} value={value} onChange={onChange} cls={cls} />
    );
  }
  if (field.type === "scholarship_gate") {
    return (
      <ScholarshipGateInput field={field} value={value} onChange={onChange} cls={cls} />
    );
  }
  if (field.type === "select") {
    return (
      <label className="block">
        <span className="text-[11px] font-medium text-stone-700 block mb-1">{field.label}</span>
        <select
          className={cls}
          value={value || ""}
          data-testid={`profile-field-${field.key}`}
          onChange={e => onChange(e.target.value || undefined)}
        >
          <option value="">—</option>
          {field.options.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </label>
    );
  }
  if (field.type === "boolean") {
    return (
      <label className="block">
        <span className="text-[11px] font-medium text-stone-700 block mb-1">{field.label}</span>
        <select
          className={cls}
          value={value === true ? "yes" : value === false ? "no" : ""}
          data-testid={`profile-field-${field.key}`}
          onChange={e => onChange(e.target.value === "yes" ? true : e.target.value === "no" ? false : undefined)}
        >
          <option value="">—</option>
          <option value="yes">Yes</option>
          <option value="no">No</option>
        </select>
      </label>
    );
  }
  if (field.type === "predicted_grades") {
    return (
      <label className="block sm:col-span-2">
        <span className="text-[11px] font-medium text-stone-700 block mb-1">{field.label}</span>
        <PredictedGradesPicker
          value={Array.isArray(value) ? value : []}
          onChange={onChange}
          compact
          testIdPrefix={`profile-field-${field.key}`}
        />
      </label>
    );
  }
  if (field.type === "textarea") {
    return (
      <label className="block sm:col-span-2">
        <span className="text-[11px] font-medium text-stone-700 block mb-1">{field.label}</span>
        <textarea
          rows={2}
          className={cls}
          value={value || ""}
          placeholder={field.placeholder}
          data-testid={`profile-field-${field.key}`}
          onChange={e => onChange(e.target.value || undefined)}
        />
      </label>
    );
  }
  if (field.type === "slider") {
    return (
      <label className="block">
        <span className="text-[11px] font-medium text-stone-700 block mb-1">{field.label}</span>
        <input
          type="range"
          min="1" max="5" step="1"
          value={value ?? 3}
          data-testid={`profile-field-${field.key}`}
          onChange={e => onChange(Number(e.target.value))}
          className="w-full"
        />
        <div className="text-[10px] text-stone-500 mt-0.5 font-mono">{value ?? 3} / 5</div>
      </label>
    );
  }
  // text / number
  return (
    <label className="block">
      <span className="text-[11px] font-medium text-stone-700 block mb-1">{field.label}</span>
      <input
        type={field.type}
        className={cls}
        value={value ?? ""}
        placeholder={field.placeholder}
        data-testid={`profile-field-${field.key}`}
        onChange={e => {
          const v = field.type === "number" ? (e.target.value ? Number(e.target.value) : undefined) : (e.target.value || undefined);
          onChange(v);
        }}
      />
    </label>
  );
}


/**
 * Currency-aware tuition input with a "Not sure yet" opt-out.
 * ------------------------------------------------------------
 *  • Value is ALWAYS stored/emitted as USD (so backend logic and
 *    the `max_annual_tuition_usd` field are unchanged).
 *  • What we DISPLAY is the value converted into the student's
 *    currently-selected currency (top-right navbar). The label
 *    reflects that currency so "USD" doesn't confuse a UAE parent
 *    who thinks in AED.
 *  • "Not sure yet" clears the value to 0 — the matching engine's
 *    tuition gate treats 0 as "no cap" so the student sees all
 *    options while they figure it out.
 */
function CurrencyOptionalInput({ field, value, onChange, cls }) {
  const { currency, rates } = useCurrency() || { currency: "USD", rates: { USD: 1 } };
  const rate = (rates && rates[currency]) || 1;

  // "Not sure yet" reveals as `value === 0` — engine treats 0 as no
  // cap. Undefined = user hasn't touched the field yet.
  const notSure = value === 0;
  const displayVal = value == null || notSure ? "" : Math.round(Number(value) * rate);
  const placeholderLocal = Math.round(20000 * rate);

  return (
    <label className="block sm:col-span-2">
      <span className="text-[11px] font-medium text-stone-700 block mb-1">
        {field.label} ({currency})
      </span>
      <div className="flex items-center gap-2">
        <input
          type="number"
          className={cls + (notSure ? " opacity-50" : "")}
          value={displayVal}
          disabled={notSure}
          placeholder={notSure ? "Any budget" : `e.g. ${placeholderLocal.toLocaleString()}`}
          data-testid={`profile-field-${field.key}`}
          onChange={e => {
            const local = e.target.value ? Number(e.target.value) : undefined;
            if (local === undefined || Number.isNaN(local)) return onChange(undefined);
            onChange(local / rate); // store as USD
          }}
        />
      </div>
      <label className="mt-1.5 inline-flex items-center gap-1.5 text-[11.5px] text-stone-600 cursor-pointer select-none">
        <input
          type="checkbox"
          className="accent-emerald-600"
          checked={notSure}
          data-testid={`profile-field-${field.key}-notsure`}
          onChange={e => onChange(e.target.checked ? 0 : undefined)}
        />
        <span>I'm not sure yet — show me all budgets</span>
      </label>
    </label>
  );
}

/**
 * Scholarship-required gate with a warning banner.
 * ------------------------------------------------
 * Behavioural nudge: everyone WANTS a scholarship, but selecting
 * "Yes" silently filtered out any university that doesn't have a
 * verified scholarship in our data. Now we make that consequence
 * explicit before they commit.
 *
 * Three states:
 *   undefined → user hasn't answered
 *   false     → won't require a scholarship (default recommended)
 *   true      → REQUIRES one; warning appears immediately
 */
function ScholarshipGateInput({ field, value, onChange, cls }) {
  return (
    <label className="block sm:col-span-2">
      <span className="text-[11px] font-medium text-stone-700 block mb-1">{field.label}</span>
      <select
        className={cls}
        value={value === true ? "yes" : value === false ? "no" : ""}
        data-testid={`profile-field-${field.key}`}
        onChange={e => {
          const v = e.target.value;
          onChange(v === "yes" ? true : v === "no" ? false : undefined);
        }}
      >
        <option value="">—</option>
        <option value="no">No — I can proceed without one</option>
        <option value="yes">Yes — I need a scholarship to attend</option>
      </select>
      {value === true && (
        <div
          data-testid={`profile-field-${field.key}-warning`}
          className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 flex items-start gap-2"
        >
          <AlertTriangle className="w-3.5 h-3.5 text-amber-700 flex-none mt-0.5" />
          <p className="text-[11.5px] text-amber-900 leading-snug">
            Heads-up: selecting <strong>Yes</strong> filters out universities we haven't verified as
            offering a scholarship that fits you. Many great courses still exist beyond that filter —
            only pick Yes if funding is a genuine dealbreaker.
          </p>
        </div>
      )}
    </label>
  );
}
